import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import FocusTrap from 'focus-trap-react';
import { Link as GatsbyLink, graphql, useStaticQuery } from 'gatsby';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Text } from '../../../../../components';
import IconLogo from '../../../../icons/icon-stark-logo';
import './style.css';

export default function Navigation({ bgColor }): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const data = useStaticQuery(graphql`
    query BlogNewQuery {
      allStrapiBlogPost(limit: 1, sort: { date: DESC }) {
        nodes {
          title
          headerImage {
            alternativeText
            formats {
              medium {
                url
              }
            }
          }
          slug
        }
      }
    }
  `);

  const handleButtonClick = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  let navText, navButton, navHover;
  if (
    bgColor === 'bone' ||
    bgColor === 'purple-100' ||
    bgColor === 'transparent'
  ) {
    navText = 'text-type-purple';
    navButton = 'bg-button-primary text-type-white';
    navHover = 'hover:border-base-purple-500 focus:border-base-purple-500';
  } else {
    navText = 'text-type-white';
    navButton = 'bg-white text-type-purple';
    navHover = 'hover:border-white focus:border-white';
  }

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', isOpen);
  }, [isOpen]);

  return (
    <>
      {/* Mobile Nav */}
      <FocusTrap active={isOpen}>
        <div className="relative z-50 flex w-full justify-between nav:hidden">
          <div className={`flex flex-row justify-between gap-x-4 ${navText}`}>
            <GatsbyLink
              to="/"
              className="transition-opacity hover:opacity-90 focus:opacity-90">
              <IconLogo aria-label="Home" />
            </GatsbyLink>
          </div>
          <div className="flex-between flex">
            <a
              className={`flex h-8 items-center whitespace-nowrap rounded-md border-2 border-transparent px-2 align-middle text-sm font-black uppercase tracking-[1.12px] transition-opacity hover:opacity-90 focus:opacity-90 ${navButton}`}
              href="https://app.getstark.co/launch-trial">
              Start Free Team Trial
            </a>
            <button
              className="nav__mobile-menu-button"
              onClick={handleButtonClick}
              aria-controls="mobile-navigation"
              aria-expanded={isOpen}
              aria-label="Open navigation">
              <div
                className={`nav__mobile-menu__hamburger ${
                  isOpen ? 'open' : ''
                }`}>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>

          <NavigationMenu.Root
            id="mobile-navigation"
            orientation="vertical"
            className={`fixed left-0 top-16 z-auto h-[calc(100%_-_64px)] w-full overflow-auto bg-base-bone ${
              isOpen ? 'block animate-mobile-navigation-fade-in' : 'hidden'
            }`}>
            <NavigationMenu.List>
              <NavigationMenu.Item className="ml-auto">
                <NavigationMenu.Link
                  className="mx-6 flex items-center whitespace-nowrap border-b border-neutral-gray-200 py-6 align-middle text-sm font-black uppercase tracking-[1.12px] text-base-purple-500"
                  href="https://app.getstark.co/sign-in">
                  Log in
                </NavigationMenu.Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item>
                <Text className="mx-6 flex items-center whitespace-nowrap pt-6 align-middle !text-sm font-black uppercase tracking-[1.12px] text-base-purple-500">
                  Use Cases
                </Text>
              </NavigationMenu.Item>
              <NavigationMenu.Item className="mx-10 border-l border-l-neutral-gray-100 px-4 py-2">
                <Link
                  href="/for-designers"
                  className="text text--semibold text-type-purple hover:underline focus:underline">
                  For Designers
                </Link>
              </NavigationMenu.Item>
              <NavigationMenu.Item className="mx-10 border-l border-l-neutral-gray-100 px-4 py-2">
                <Link
                  href="/for-developers"
                  className="text text--semibold text-type-purple hover:underline focus:underline">
                  For Developers
                </Link>
              </NavigationMenu.Item>
              <NavigationMenu.Item className="mx-10 border-l border-l-neutral-gray-100 px-4 py-2">
                <Link
                  href="/for-product-managers"
                  className="text text--semibold text-type-purple hover:underline focus:underline">
                  For Product Managers
                </Link>
              </NavigationMenu.Item>
              <NavigationMenu.Item className="mx-10 border-l border-l-neutral-gray-100 px-4 py-2">
                <Link
                  href="/for-compliance-managers"
                  className="text text--semibold text-type-purple hover:underline focus:underline">
                  For Compliance Managers
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item className="mx-6 flex items-center whitespace-nowrap border-b border-neutral-gray-200 py-6 align-middle text-sm font-black uppercase tracking-[1.12px] text-base-purple-500">
                <Link href="/pricing/" bgColor={bgColor}>
                  Pricing
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item className="mx-6 flex items-center whitespace-nowrap border-b border-neutral-gray-200 py-6 align-middle text-sm font-black uppercase tracking-[1.12px] text-base-purple-500">
                <Link href="/support/" bgColor={bgColor}>
                  Support
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item className="mx-6 flex items-center whitespace-nowrap border-b border-neutral-gray-200 py-6 align-middle text-sm font-black uppercase tracking-[1.12px] text-base-purple-500">
                <Link href="/blog/" bgColor={bgColor}>
                  Blog
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item className="mx-6 flex items-center whitespace-nowrap border-b border-neutral-gray-200 py-6 align-middle text-sm font-black uppercase tracking-[1.12px] text-base-purple-500">
                <Link href="/library/" bgColor={bgColor}>
                  Library
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item className="mx-6 flex items-center whitespace-nowrap border-b border-neutral-gray-200 py-6 align-middle text-sm font-black uppercase tracking-[1.12px] text-base-purple-500">
                <Link href="/wcag-explained/" bgColor={bgColor}>
                  The WCAG Explained
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item className="mx-6 flex items-center whitespace-nowrap border-b border-neutral-gray-200 py-6 align-middle text-sm font-black uppercase tracking-[1.12px] text-base-purple-500">
                <Link href="/community/" bgColor={bgColor}>
                  Community
                </Link>
              </NavigationMenu.Item>
            </NavigationMenu.List>
          </NavigationMenu.Root>
        </div>
      </FocusTrap>

      {/* Desktop Nav */}
      <NavigationMenu.Root className="relative z-50 hidden w-full justify-center nav:flex [&>div]:min-w-full">
        <NavigationMenu.List
          className={`flex flex-row justify-between gap-x-2 lg:gap-x-4 ${navText}`}>
          <NavigationMenu.Item>
            <Link
              href="/"
              className="transition-opacity hover:opacity-90 focus:opacity-90">
              <IconLogo aria-label="Home" />
            </Link>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <NavigationMenu.Trigger
              className={`flex h-8 items-center whitespace-nowrap rounded-md border-2 border-transparent px-2 align-middle text-sm font-black uppercase tracking-[1.12px] ${navHover}`}>
              Use Cases{' '}
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="ml-1">
                <path
                  d="M6.79669 8.0716C6.41769 8.6428 5.58231 8.6428 5.20331 8.0716L3.16242 4.9957C2.73821 4.35637 3.19436 3.5 3.95911 3.5L8.04089 3.5C8.80564 3.5 9.26179 4.35637 8.83758 4.9957L6.79669 8.0716Z"
                  fill="currentColor"
                />
              </svg>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className="data-[motion=from-start]:animate-enterFromLeft data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToLeft data-[motion=to-end]:animate-exitToRight absolute left-0 top-0 w-full sm:w-auto">
              <ul className="one m-0 grid w-[788px] list-none grid-cols-3 gap-x-6 p-2">
                <ListItem href="/for-designers/" title="Designers">
                  Create accessible designs in record time right in Figma &
                  Sketch.
                </ListItem>
                <ListItem
                  href="/for-product-managers/"
                  title="Product Managers">
                  Manage accessibility in real-time across all your projects.
                </ListItem>
                <li className="row-span-2">
                  <NavigationMenu.Link asChild>
                    <GatsbyLink
                      to="/stark-whitepaper-q1-2024"
                      className="flex h-full grow flex-col items-center justify-between rounded-md border bg-purple-100 p-3 text-sm transition-colors hover:border-purple-300 focus:border-purple-300">
                      <img
                        src="https://images.getstark.co/marketing/accessibility-posture-white-paper/white-paper-callout.png?updatedAt=1709743608630"
                        alt=""
                        className="h-20 pb-1.5"
                      />
                      <div>
                        <Text level="semibold" className="!leading-[1.4]">
                          Read our new white paper
                        </Text>
                        <Text className="!text-[13px] !leading-[140%] !tracking-[3%]">
                          Managing a modern accessibility posture and growing
                          accessibility maturity at any scale
                        </Text>
                      </div>
                    </GatsbyLink>
                  </NavigationMenu.Link>
                </li>
                <ListItem href="/for-developers/" title="Developers">
                  Test and audit your code from early implementation to live
                  product.
                </ListItem>
                <ListItem
                  href="/for-compliance-managers/"
                  title="Compliance Managers">
                  Monitor, manage and audit your accessibility posture in one
                  place.
                </ListItem>
              </ul>
            </NavigationMenu.Content>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <Link href="/pricing/" bgColor={bgColor}>
              Pricing
            </Link>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <Link href="/support/" bgColor={bgColor}>
              Support
            </Link>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <NavigationMenu.Trigger
              className={`flex h-8 items-center whitespace-nowrap rounded-md border-2 border-transparent px-2 align-middle text-sm font-black uppercase tracking-[1.12px] ${navHover}`}>
              Resources{' '}
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="ml-1">
                <path
                  d="M6.79669 8.0716C6.41769 8.6428 5.58231 8.6428 5.20331 8.0716L3.16242 4.9957C2.73821 4.35637 3.19436 3.5 3.95911 3.5L8.04089 3.5C8.80564 3.5 9.26179 4.35637 8.83758 4.9957L6.79669 8.0716Z"
                  fill="currentColor"
                />
              </svg>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className="absolute left-0 top-0 w-full sm:w-auto">
              <ul className="two m-0 grid w-[788px] list-none grid-cols-3 gap-x-6 p-2">
                <li className="row-span-3">
                  {data.allStrapiBlogPost.nodes.map(
                    ({ slug, title, headerImage }) => {
                      return (
                        <NavigationMenu.Link asChild>
                          <GatsbyLink
                            to={`/blog/${slug}`}
                            className="flex h-full grow flex-col rounded-md border bg-purple-100 text-sm transition-colors hover:border-purple-300 focus:border-purple-300">
                            <div className="overflow-hidden">
                              <img
                                className="h-36 w-60 !rounded-t-md object-cover"
                                src={headerImage?.formats.medium.url}
                                alt={headerImage?.alternativeText}
                              />
                            </div>
                            <div className="p-3">
                              <Text
                                level="semibold"
                                className="!leading-[140%]">
                                {title}
                              </Text>
                            </div>
                            <div className="mt-auto px-3 pb-2">
                              <Text
                                className="!text-[13px] !leading-[140%] !tracking-[3%] underline"
                                color="purple">
                                Read more on the blog
                              </Text>
                            </div>
                          </GatsbyLink>
                        </NavigationMenu.Link>
                      );
                    }
                  )}
                </li>
                <ListItem href="/blog/" title="Blog">
                  Read about the latest updates to Stark and articles from our
                  team.
                </ListItem>
                <ListItem href="/support/" title="Support Center">
                  Find answers to your questions plus tips and tricks on how to
                  use Stark.
                </ListItem>
                <ListItem href="/library/" title="Library">
                  Learn about all things accessibility and inclusive design.
                </ListItem>
                <ListItem href="/community/" title="Community">
                  Join more than 3,500 accessibility folks from around the
                  world.
                </ListItem>
                <ListItem href="/wcag-explained/" title="The WCAG Explained">
                  Simple explanations of every WCAG criteria.
                </ListItem>
                <ListItem
                  href="/stark-whitepaper-q1-2024/"
                  title="Stark White Paper">
                  Managing and growing your accessibility posture at any scale.
                </ListItem>
              </ul>
            </NavigationMenu.Content>
          </NavigationMenu.Item>

          <NavigationMenu.Item className="ml-auto">
            <NavigationMenu.Link
              className={`flex h-8 items-center whitespace-nowrap rounded-md border-2 border-transparent px-2 align-middle text-sm font-black uppercase tracking-[1.12px] ${navHover}`}
              href="https://app.getstark.co/sign-in">
              Log in
            </NavigationMenu.Link>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <NavigationMenu.Link
              className={`flex h-8 items-center whitespace-nowrap rounded-md border-2 border-transparent px-2 align-middle text-sm font-black uppercase tracking-[1.12px]  transition-opacity hover:opacity-90 focus:opacity-90 ${navButton}`}
              href="https://app.getstark.co/launch-trial">
              Start Free Team Trial
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        </NavigationMenu.List>

        <div className="perspective-[2000px] absolute top-full z-50 flex w-full justify-center min-[980px]:-left-20">
          <NavigationMenu.Viewport className="relative mt-[10px] h-[var(--radix-navigation-menu-viewport-height)] w-full origin-top animate-navigation-fade-in rounded-md bg-white shadow-md transition-[width,_height] duration-300 data-[state=closed]:animate-navigation-fade-out sm:w-[var(--radix-navigation-menu-viewport-width)]" />
        </div>
      </NavigationMenu.Root>
    </>
  );
}

const Link = ({
  href,
  bgColor,
  ...props
}: {
  href: string;
  bgColor?: string;
  className?: string;
  children: ReactNode;
}) => {
  let navHover;
  if (
    bgColor === 'bone' ||
    bgColor === 'purple-100' ||
    bgColor === 'transparent'
  ) {
    navHover =
      'nav:hover:border-base-purple-500 nav:focus:border-base-purple-500';
  } else {
    navHover = 'nav:hover:border-white nav:focus:border-white';
  }

  return (
    <NavigationMenu.Link asChild>
      <GatsbyLink
        to={href}
        className={`flex h-8 items-center whitespace-nowrap rounded-md border-2 border-transparent px-2 align-middle text-sm font-black uppercase tracking-[1.12px] transition-colors ${navHover}`}
        {...props}
      />
    </NavigationMenu.Link>
  );
};

const ListItem = React.forwardRef(
  ({ className, title, children, href, ...props }: any, forwardedRef) => (
    <li>
      <NavigationMenu.Link asChild>
        <GatsbyLink
          to={href}
          className="flex h-full grow flex-col rounded-md p-3 align-middle text-sm transition-colors hover:bg-purple-50 focus:bg-purple-50"
          {...props}>
          <Text level="semibold" color="purple">
            {title}
          </Text>
          <Text className="!text-[13px] !leading-[140%] !tracking-[3%]">
            {children}
          </Text>
        </GatsbyLink>
      </NavigationMenu.Link>
    </li>
  )
);
